import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import PageHero from "@/components/PageHero";
import FinalCTA from "@/components/FinalCTA";
import {
  CheckCircle2, ArrowLeft, ArrowRight, ArrowUpRight, Briefcase,
  Layers, Lightbulb, Rocket, ShieldCheck, Wrench
} from "lucide-react";
import { getPartnerBySlug, PARTNERS, ALLIANCE_NETWORK } from "@/data/partners";

export default function AlliancePartner() {
  const { slug } = useParams();
  const partner = getPartnerBySlug(slug);

  if (!partner) return <Navigate to="/alliances" replace />;

  const related = PARTNERS
    .filter((p) => p.slug !== partner.slug && p.networks.some((n) => partner.networks.includes(n)))
    .slice(0, 3);

  const networkLayers = partner.networks
    .map((n) => ALLIANCE_NETWORK.find((l) => l.id === n))
    .filter(Boolean);

  return (
    <div data-testid="alliance-partner-page">
      <PageHero
        eyebrow={`${partner.category} · Alliance Partner`}
        title={partner.name}
        description={partner.tagline}
      >
        <div className="flex flex-wrap gap-3 items-center">
          <Link to="/contact?type=consultation" className="btn-accent" data-testid="partner-hero-cta">
            Request Deployment Plan <ArrowUpRight size={16} />
          </Link>
          <Link to="/alliances" className="btn-ghost" data-testid="partner-back-link">
            <ArrowLeft size={14} /> Alliance ecosystem
          </Link>
        </div>
      </PageHero>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="container-cd grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <span className="badge-cd">What it does</span>
            <h2 className="mt-4 font-display text-2xl md:text-3xl font-bold text-[#0A1B5E] leading-tight">
              {partner.name} at a glance
            </h2>
            <p className="mt-5 text-slate-700 text-lg leading-relaxed">{partner.what}</p>
          </div>
          <aside className="card-cd !p-7 h-fit lg:sticky lg:top-32" data-testid="partner-quickfacts">
            <div className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-400">Quick Facts</div>
            <dl className="mt-4 space-y-4 text-sm">
              <div>
                <dt className="text-slate-500">Category</dt>
                <dd className="font-display font-semibold text-[#0A1B5E]">{partner.category}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Tagline</dt>
                <dd className="font-display font-semibold text-[#0A1B5E]">{partner.tagline}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Alliance Layers</dt>
                <dd className="mt-1 flex flex-wrap gap-1.5">
                  {networkLayers.map((l) => (
                    <span key={l.id} className="px-2 py-1 rounded-full text-[10px] font-mono-tech tracking-wider uppercase bg-blue-50 text-[#3B82F6] border border-blue-100">
                      {l.title.split(" ")[0]}
                    </span>
                  ))}
                </dd>
              </div>
            </dl>
            <Link to="/contact?type=consultation" className="btn-primary w-full justify-center mt-6 text-sm" data-testid="partner-quickfacts-cta">
              Talk to a specialist <ArrowRight size={14} />
            </Link>
          </aside>
        </div>
      </section>

      {/* 4-panel deep dive */}
      <section className="py-16 bg-[#F8FAFC]" data-testid="partner-deepdive">
        <div className="container-cd grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card-cd !p-8" data-testid="partner-benefits">
            <span className="h-12 w-12 grid place-items-center rounded-xl bg-blue-50 border border-blue-100 text-[#3B82F6]">
              <ShieldCheck size={22} strokeWidth={1.6} />
            </span>
            <h3 className="mt-5 font-display text-xl font-bold text-[#0A1B5E]">Enterprise benefits</h3>
            <ul className="mt-5 space-y-3">
              {partner.benefits.map((b) => (
                <li key={b} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle2 size={16} className="text-[#3B82F6] mt-0.5 shrink-0" />
                  <span className="text-[15px]">{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="card-cd !p-8" data-testid="partner-usecases">
            <span className="h-12 w-12 grid place-items-center rounded-xl bg-violet-50 border border-violet-100 text-[#6D28D9]">
              <Briefcase size={22} strokeWidth={1.6} />
            </span>
            <h3 className="mt-5 font-display text-xl font-bold text-[#0A1B5E]">Enterprise use cases</h3>
            <ul className="mt-5 space-y-3">
              {partner.useCases.map((u) => (
                <li key={u} className="flex items-start gap-3 text-slate-700">
                  <Layers size={16} className="text-[#6D28D9] mt-0.5 shrink-0" />
                  <span className="text-[15px]">{u}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="card-cd !p-8" data-testid="partner-deployment">
            <span className="h-12 w-12 grid place-items-center rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-600">
              <Rocket size={22} strokeWidth={1.6} />
            </span>
            <h3 className="mt-5 font-display text-xl font-bold text-[#0A1B5E]">Deployment value</h3>
            <p className="mt-5 text-slate-700 leading-relaxed text-[15px]">{partner.deploymentValue}</p>
          </div>

          <div className="card-cd !p-8 ring-glow-cyan border-cyan-200" data-testid="partner-cosmos-support">
            <span className="h-12 w-12 grid place-items-center rounded-xl gradient-cta text-white">
              <Wrench size={22} strokeWidth={1.6} />
            </span>
            <h3 className="mt-5 font-display text-xl font-bold text-[#0A1B5E]">How Cosmos Defence supports implementation</h3>
            <ul className="mt-5 space-y-3">
              {partner.cosmosSupport.map((c) => (
                <li key={c} className="flex items-start gap-3 text-slate-700">
                  <Lightbulb size={16} className="text-[#22D3EE] mt-0.5 shrink-0" />
                  <span className="text-[15px]">{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Related partners */}
      {related.length > 0 && (
        <section className="py-16 bg-white" data-testid="partner-related">
          <div className="container-cd">
            <h3 className="font-display text-2xl md:text-3xl font-bold text-[#0A1B5E]">Related alliance partners</h3>
            <p className="mt-3 text-slate-600">Other partners working alongside {partner.name} in the Cosmos Defence ecosystem.</p>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((p, i) => (
                <Link
                  to={`/alliances/${p.slug}`}
                  key={p.slug}
                  data-testid={`partner-related-${i}`}
                  className="card-cd group"
                >
                  <div className="flex items-center justify-between">
                    <div className="font-display text-lg font-extrabold text-[#0A1B5E]">{p.name}</div>
                    <span className="font-mono-tech text-[10px] tracking-widest uppercase text-[#3B82F6] bg-blue-50 px-2 py-1 rounded-full border border-blue-100">
                      {p.category.split("/")[0].trim()}
                    </span>
                  </div>
                  <p className="mt-3 text-slate-600 text-sm leading-relaxed">{p.tagline}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-[#3B82F6] font-semibold text-sm group-hover:gap-2 transition-all">
                    View profile <ArrowRight size={14} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <FinalCTA />
    </div>
  );
}
